import { useQuery, useQueryClient } from '@tanstack/react-query'
import { EmptyState, ErrorState, Table, TableSearch, TableToolbar, Text } from '@cinesend/ui-kit'
import { Trash } from '@phosphor-icons/react'
import { Link, useNavigate } from 'react-router-dom'
import RowMenu from '../row_menu'
import useTableFilters from '../../hooks/use_table_filters'
import useStore from '../../hooks/use_store'

const UsersTable = ({ queryKey }) => {
  const { queryString, searching } = useTableFilters({
    enableSearch: true,
  })
  const { data, error, isLoading } = useQuery([queryKey, queryString])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { deleteModel } = useStore()

  const users = data?.data || []
  const refetch = () => queryClient.invalidateQueries([queryKey, queryString])

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (user) => (
        <Text>
          <Link className='hover:underline' to={`/settings/users/${user.id}`}>
            {user.name}
          </Link>
        </Text>
      ),
    },
    { key: 'email', header: 'Email', render: (user) => user.email },
    {
      key: 'organization',
      header: 'Organization',
      render: (user) => user.organization?.name || 'N/A',
    },
    { key: 'role', header: 'Role', render: (user) => user.role?.label || 'N/A' },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (user) => (
        <RowMenu
          label={`Actions for ${user.name}`}
          items={[
            {
              id: 'delete',
              label: 'Delete User',
              icon: <Trash size={20} />,
              tone: 'danger',
              onSelect: () =>
                deleteModel.mutate({
                  endpoint: `users`,
                  id: user.id,
                  deleteNoun: 'user',
                  deleteName: user.name,
                  onSuccess: refetch,
                }),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <TableToolbar>
        <TableSearch value={searching.search} onValueChange={searching.onSearch} placeholder='Search users' />
      </TableToolbar>
      {error ? (
        <ErrorState message={error.message} onRetry={refetch} />
      ) : (
        <Table
          caption='Users'
          columns={columns}
          rows={users}
          rowKey={(user) => String(user.id)}
          // The kit ignores row clicks that start on the name link or the Actions menu, so neither navigates twice
          onRowClick={(user) => navigate(`/settings/users/${user.id}`)}
          isLoading={isLoading}
          empty={<EmptyState title='No users found!' detail='Create a user to begin.' />}
        />
      )}
    </>
  )
}

export default UsersTable
